import { createFileRoute, Link } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { useState } from "react";
import { ArrowRight } from "lucide-react";

import { listQuery } from "@/lib/queries";
import { PageHero, Section, EmptyState } from "@/components/site/bits";
import { formatDate } from "@/lib/format";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/noticias")({
  head: () => ({
    meta: [
      { title: "Notícias | Professor Rurdiney — 40123" },
      {
        name: "description",
        content: "Acompanhe as últimas notícias da campanha do Professor Rurdiney, candidato a vereador nº 40123.",
      },
      { property: "og:title", content: "Notícias | Professor Rurdiney — 40123" },
      {
        property: "og:description",
        content: "Fique por dentro das novidades da campanha do Professor Rurdiney.",
      },
    ],
  }),
  loader: ({ context }) =>
    Promise.all([
      context.queryClient.ensureQueryData(listQuery("news")),
      context.queryClient.ensureQueryData(listQuery("news_categories")),
    ]),
  component: NoticiasPage,
});

function NoticiasPage() {
  const { data: news } = useSuspenseQuery(listQuery("news"));
  const { data: categories } = useSuspenseQuery(listQuery("news_categories"));
  const [active, setActive] = useState<string | null>(null);

  const filtered = active ? news.filter((n: any) => n.category_id === active) : news;
  const featured = filtered.find((n: any) => n.featured) || filtered[0];
  const rest = filtered.filter((n: any) => n.id !== featured?.id);

  return (
    <>
      <PageHero
        eyebrow="Imprensa"
        title="Notícias"
        description="Acompanhe as novidades, bastidores e conquistas da campanha do Professor Rurdiney."
      />

      <Section>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setActive(null)}
            className={cn(
              "rounded-full border px-4 py-2 text-sm font-semibold uppercase tracking-wide transition-colors",
              active === null
                ? "border-primary bg-primary text-primary-foreground"
                : "border-border text-muted-foreground hover:border-primary hover:text-primary",
            )}
          >
            Todas
          </button>
          {categories.map((c: any) => (
            <button
              key={c.id}
              onClick={() => setActive(c.id)}
              className={cn(
                "rounded-full border px-4 py-2 text-sm font-semibold uppercase tracking-wide transition-colors",
                active === c.id
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border text-muted-foreground hover:border-primary hover:text-primary",
              )}
            >
              {c.name}
            </button>
          ))}
        </div>

        {!featured ? (
          <div className="mt-10">
            <EmptyState message="Nenhuma notícia encontrada." />
          </div>
        ) : (
          <>
            <Link
              to="/noticias/$slug"
              params={{ slug: featured.slug }}
              className="group mt-10 flex flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-card transition-all hover:shadow-glow md:flex-row"
            >
              <div className="flex flex-1 flex-col justify-center bg-primary p-8 text-primary-foreground md:p-10">
                <span className="inline-flex w-fit rounded-full bg-accent px-3 py-1 text-xs font-bold uppercase tracking-wide text-accent-foreground">
                  Destaque
                </span>
                <h2 className="mt-4 text-2xl md:text-4xl text-balance-title">{featured.title}</h2>
                <p className="mt-3 text-sm leading-relaxed text-primary-foreground/85 md:text-base">
                  {featured.excerpt}
                </p>
                <div className="mt-5 text-xs uppercase tracking-wide text-primary-foreground/70">
                  {formatDate(featured.published_at)}
                  {featured.author ? ` • ${featured.author}` : ""}
                </div>
                <span className="mt-5 inline-flex w-fit items-center gap-1 text-sm font-bold uppercase tracking-wide text-accent">
                  Ler matéria completa <ArrowRight className="size-4" />
                </span>
              </div>
            </Link>

            <div className="mt-8 grid gap-6 md:grid-cols-3">
              {rest.map((n: any) => (
                <Link
                  key={n.id}
                  to="/noticias/$slug"
                  params={{ slug: n.slug }}
                  className="group flex flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-card transition-all hover:shadow-glow"
                >
                  <div className="flex-1 p-6">
                    <span className="text-xs font-bold uppercase tracking-wide text-accent-deep">
                      {formatDate(n.published_at)}
                    </span>
                    <h3 className="mt-2 text-lg text-foreground">{n.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{n.excerpt}</p>
                    {n.author && (
                      <p className="mt-3 text-xs uppercase tracking-wide text-muted-foreground">
                        Por {n.author}
                      </p>
                    )}
                  </div>
                  <div className="border-t border-border px-6 py-3">
                    <span className="inline-flex items-center gap-1 text-sm font-bold uppercase tracking-wide text-primary group-hover:text-accent-deep">
                      Ler mais <ArrowRight className="size-4" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </>
        )}
      </Section>
    </>
  );
}
